/**
 * Reglas puras del Mapa de Talento que salieron de incidentes del ciclo 2026
 * (Sol, 18/09). Sin AWS ni React: se testean con `npm run test:talent-rules`.
 */
import { normalizeEmail } from './identity';
import { foldKey } from './text-normalize';
import { isClosedTalentStatus } from './types/talent-map';

const norm = (s: string) => foldKey(s);

export interface SwitchCandidate {
  email: string;
  name: string;
  lastName: string;
}

/**
 * ¿El líder está pidiendo evaluar a OTRA persona dentro de esta conversación?
 *
 * Incidente 17/09 (Rodrigo Álvarez): el agente preguntó "¿otra persona?", el
 * líder dijo "Sí, a Paula Endrizzi" y toda la evaluación de Paula quedó
 * guardada en el registro de Marcos. Otro líder respondió "continúo" a
 * "¿seguimos con esta persona?" y pasó lo mismo. Una conversación = una
 * persona: si en un mensaje corto nombra a otro de sus directos (o responde a
 * esa pregunta con un apellido), no se llama al modelo y se devuelve a dónde ir.
 *
 * Mensajes largos (>14 palabras) no cuentan: ahí el nombre de un compañero
 * suele ser evidencia ("trabajó con Paula en el proyecto X").
 */
export function detectSwitchRequest(
  message: string,
  currentEmail: string,
  directs: SwitchCandidate[],
  lastAssistant: string,
): { email: string; name: string } | null {
  const msg = norm(message);
  if (!msg) return null;
  const words = msg.split(/\s+/).filter(Boolean).length;
  const askedForAnother = /otra persona|alguien mas|siguiente persona|queres evaluar|quieres evaluar|evaluar a otr|seguimos con|continuamos con/.test(norm(lastAssistant));
  // "No, prefiero seguir. Pérez la ayudó igual" ⇒ se queda con la persona actual.
  if (/^(no|nop|nope|todavia no|aun no|por ahora no)\b/.test(msg)) return null;
  // Sin "a " como disparador: "A Juan Pérez lo ayudó mucho…" es evidencia, no un cambio.
  const intent = /^(si|dale|ok|okey|bueno|ahora|sigamos|seguimos|vamos|evaluemos|quiero evaluar|pasemos|continuo|continuemos)\b/.test(msg)
    || /\b(evaluar|evaluemos|sigamos|seguimos|pasemos|continuemos|vamos) (a|con) /.test(msg);
  if (words > 14 || !(askedForAnother || intent)) return null;

  const cur = currentEmail.toLowerCase();
  for (const r of directs) {
    if (r.email.toLowerCase() === cur) continue;
    const first = norm(r.name).split(/\s+/)[0];
    const last = norm(r.lastName).split(/\s+/)[0];
    if (!first || !last) continue;
    const hasWord = (w: string) => new RegExp(`(^|[^a-z])${w}([^a-z]|$)`).test(msg);
    const full = hasWord(first) && hasWord(last);
    const lastOnly = askedForAnother && last.length >= 4 && hasWord(last);
    const firstOnly = askedForAnother && first.length >= 4 && hasWord(first)
      && directs.filter(d => d.email.toLowerCase() !== cur && norm(d.name).split(/\s+/)[0] === first).length === 1;
    if (full || lastOnly || firstOnly) {
      return { email: r.email.toLowerCase(), name: `${r.name} ${r.lastName}`.trim() };
    }
  }
  return null;
}

const COMPLETED: ReadonlySet<string> = new Set(['confirmed', 'hrbp_modified']);
const IN_PROGRESS: ReadonlySet<string> = new Set(['draft', 'pending_evidence']);

/**
 * Una persona puede tener records en más de una partición LEADER# (reorgs:
 * el cron la auto-asignó bajo su líder de entonces y después la evaluó su
 * líder nuevo — Gardonio/Bellingeri 17/09; o pm y N+1 evaluando en paralelo —
 * Lucía Jorge 18/09). Para la vista de People vale UNO: una evaluación
 * COMPLETADA por sobre una en curso, y esa por sobre un auto_assigned /
 * not_started; a igual estado, la del líder actual del community map; después
 * la más reciente. Si hay dos confirmadas distintas, gana la del líder actual y
 * People decide (la UI avisa al segundo evaluador antes de que pase).
 */
export function pickCurrentRecord<T extends { leaderEmail: string; updatedAt: string; processStatus: string }>(
  list: T[],
  currentLeaderEmail: string,
): T | undefined {
  if (list.length <= 1) return list[0];
  const cur = normalizeEmail(currentLeaderEmail);
  const score = (r: T) =>
    (COMPLETED.has(r.processStatus) ? 4 : IN_PROGRESS.has(r.processStatus) ? 2 : 0)
    + (cur && normalizeEmail(r.leaderEmail) === cur ? 1 : 0);
  return [...list].sort((a, b) => score(b) - score(a) || (b.updatedAt || '').localeCompare(a.updatedAt || ''))[0];
}

/**
 * Cambio propuesto por el líder sobre una evaluación ya CERRADA (Sol 25/09/2026:
 * "queda la categoría confirmada por los líderes... debe reflejar en ambas
 * pantallas lo que haya dicho y confirmado el líder").
 *
 * Camila (24/09) ajustó categorías en el chat de evaluaciones ya confirmadas:
 * el agente guardó la propuesta pero no toca estados cerrados (incidente
 * 07/09), la pestaña escondía el botón de confirmar y el Mapa seguía con la
 * categoría vieja. Devuelve la categoría propuesta si difiere de la
 * confirmada y la evaluación no está bloqueada por promoción; null si no hay
 * nada pendiente. La usan el backend (nota en la respuesta del chat) y la
 * pestaña (card de confirmar + banner).
 */
export function pendingLeaderChange(
  r: {
    processStatus?: string | null;
    finalCategory?: string | null;
    leaderProposedCategory?: string | null;
    /** ISO de la propuesta (processMessage la escribe desde el 25/09). */
    leaderProposedAt?: string | null;
    /** ISO del último override de People. */
    hrbpModifiedAt?: string | null;
    systemLock?: unknown;
  } | null | undefined,
): string | null {
  if (!r || !isClosedTalentStatus(r.processStatus)) return null;
  if (r.systemLock) return null;
  const proposed = r.leaderProposedCategory ?? null;
  if (!proposed || !r.finalCategory || proposed === r.finalCategory) return null;
  // Tras un override de People la propuesta VIEJA del líder queda guardada y
  // distinta de la final: no es un cambio nuevo. Solo cuenta una propuesta
  // posterior al override; sin fecha no se puede saber ⇒ no se pisa a People
  // (code-reviewer 25/09: un clic revertía el override en silencio).
  if (r.hrbpModifiedAt && (!r.leaderProposedAt || r.leaderProposedAt <= r.hrbpModifiedAt)) return null;
  return proposed;
}

export interface TalentCategoryChange {
  from: string;
  to: string;
  /** ISO del snapshot que cambió la categoría. */
  at: string;
  by: string;
  source: string;
  versionNumber: number;
}

/**
 * Cambios de categoría de una persona a partir de sus snapshots (Sol 25/09:
 * "solo el nombre y de qué a qué categoría cambió, después de la inicial").
 * La inicial es la primera evaluación real (líder o People): pasar de
 * "Cumple con oportunidades · sin confirmar" a lo que el líder confirmó NO es
 * un cambio. Los snapshots `system` previos (auto-asignación del cron) tampoco
 * cuentan como evaluación inicial. Desde ahí, cada snapshot cuya categoría
 * difiere del anterior es un cambio (re-confirmación, override de People o
 * baja automática por promoción). VersionTimeline muestra todas las versiones;
 * esto es la lista compacta del Histórico.
 */
export function snapshotCategoryChanges(
  snapshots: ReadonlyArray<{ versionNumber: number; timestamp: string; category: string; source?: string; updatedBy?: string }> | null | undefined,
): TalentCategoryChange[] {
  const sorted = [...(snapshots ?? [])]
    .filter(s => !!s.category)
    .sort((a, b) => a.versionNumber - b.versionNumber || (a.timestamp || '').localeCompare(b.timestamp || ''));
  const first = sorted.findIndex(s => s.source !== 'system');
  if (first < 0) return [];
  const out: TalentCategoryChange[] = [];
  for (let i = first + 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const cur = sorted[i];
    if (cur.category !== prev.category) {
      out.push({ from: prev.category, to: cur.category, at: cur.timestamp, by: cur.updatedBy ?? '', source: cur.source ?? 'leader', versionNumber: cur.versionNumber });
    }
  }
  return out;
}

/**
 * Frases con las que el agente dice haber registrado la categoría. Cuando la
 * evaluación ya está cerrada, el chat NO puede cambiarla (incidente 07/09), así
 * que decir "¡Listo! Registré a X como Y" es mentira: el líder se va creyendo
 * que cambió (incidente Rosario/Lucas, 24/09/2026 — la categoría siguió siendo
 * la vieja). Se sacan del texto antes de mostrarlo y de leerlo en voz alta.
 */
const CIERRE_FALSO = /(^|[\s*_>-])(¡?\s*listo\s*!?|registr[ée]|actualic[ée]|ya (la )?(registr|actualic|guard)[ée]|qued[óo] (guardad|registrad|confirmad)|est[áa] (guardad|registrad)|i['’]?ve (recorded|registered|updated|saved)|(is|has been) (now )?(recorded|registered|saved|updated)|est[áa] (registrada|salva)|registrei|atualizei|ficou (registrad|salv))/i;

/** ¿Esta línea afirma que el cambio quedó registrado? */
export function afirmaRegistro(linea: string): boolean {
  return CIERRE_FALSO.test(linea);
}

/**
 * Saca del texto del agente las líneas que afirman haber registrado el cambio.
 * Devuelve el texto saneado y si se tocó algo (para loguearlo). Puro y testeado.
 */
export function sinCierreFalso(texto: string): { texto: string; saneado: boolean } {
  const lineas = texto.split('\n');
  const quedan = lineas.filter(l => !(l.trim() && afirmaRegistro(l)));
  const saneado = quedan.length !== lineas.length;
  // Sin líneas útiles (la respuesta era solo el "listo"), queda vacío: el caller
  // antepone igual la nota determinística con lo que hay que hacer.
  return { texto: quedan.join('\n').replace(/\n{3,}/g, '\n\n').trim(), saneado };
}

/**
 * Lo que el chat del agente propone escribir en la ficha (bloque
 * MAPA_TALENTO_ACTION ya parseado). Genérico para no duplicar acá los tipos de
 * categoría/confianza del record: el router pasa los suyos.
 */
export type CamposDeChat = Record<string, unknown> & {
  leaderProposedCategory?: unknown;
  leaderProposedAt?: unknown;
};

/**
 * Qué campos puede escribir el CHAT en la ficha. Con la evaluación ya cerrada,
 * SOLO la propuesta del líder: la evidencia, las fortalezas, las oportunidades y
 * el próximo paso describen la categoría nueva, y escribirlos encima de la
 * confirmada deja la ficha diciendo dos cosas a la vez (incidente Rosario/Lucas,
 * 24/09/2026: el Mapa mostraba Top Performer y la ficha "Registrada como
 * Performance Sólida"). Esos campos se escriben recién cuando el líder confirma
 * con el botón. Campo nuevo ⇒ decidir acá de qué lado cae.
 */
export function camposDeChat<T extends CamposDeChat>(campos: T, cerrada: boolean): Partial<T> {
  if (!cerrada) return campos;
  const { leaderProposedCategory, leaderProposedAt } = campos;
  return {
    ...(leaderProposedCategory ? { leaderProposedCategory } : {}),
    ...(leaderProposedAt ? { leaderProposedAt } : {}),
  } as Partial<T>;
}
